import { useCallback, useMemo, useRef, useState } from 'react';

import { cn } from '@utils/cn';
import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button, Textarea, TwFileUpload, Icon } from '@ui-component-library';

import { deleteAdAttachmentsNew } from '../../../../api/services/ad';
import { submitSellTimberAd } from './helpers/api';
import {
  ACTION_OPTIONS,
  buildSellTimberSuggestedComment,
  formatSvLocaleNumber,
  SALE_OPTIONS,
  SELL_TIMBER_GENERIC_ERROR,
  excludeGrotMarkerFromTimberPlacedMarkers
} from './helpers/sellTimberShared';
import {
  SELL_TIMBER_FIELD_INFO,
  SELL_TIMBER_SALE_INFO,
  SellTimberFieldInfo
} from './components/SellTimberFieldInfo.js';
import SellTimberMap, {
  SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP4
} from './components/SellTimberMap';
import SellTimberVirkesResponsiveTable, { sectionsUseExtraSpecies } from './components/SellTimberVirkesResponsiveTable';
import { SummaryEditButton, SummaryRow } from './components/SellTimberSummary';
import { collectFeaturesFromMapSelection } from './helpers/sellTimberMapSelection';

/** Stable fallback so TwFileUpload’s `value` prop is not a new `[]` every render. */
const EMPTY_ATTACHMENTS = [];

function labelFor(options, value) {
  const o = options.find((x) => x.value === value);
  return o?.label ?? value ?? '-';
}

export default function SellTimberStep4({ wizard, dispatch }) {
  const adCreateData = wizard.adCreateData;
  const geometry = wizard.geometry;
  const forestWithSections = wizard.forestWithSections;
  const existingAdId = wizard.editingAdId;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /** Keeps latest draft without putting it in useCallback deps — Textarea calls onChange in an effect when `onChange` identity changes, which would loop with SET_AD_CORE. */
  const adCreateDataRef = useRef(adCreateData);
  adCreateDataRef.current = adCreateData;

  const volumeRows = forestWithSections?.sections ?? [];
  const showExtraCols = sectionsUseExtraSpecies(volumeRows);
  const virkesLabelsForMap = volumeRows.map((r) => (r?.name != null ? String(r.name) : ''));

  /** Same ordering as steg 3 “Hämta från kartan” — valda avdelningar, avverkningsytor, ritade polygoner, ev. utkast. */
  const mapSelectionFeatureCount = useMemo(
    () => collectFeaturesFromMapSelection(geometry).length,
    [geometry]
  );

  const handleCommentsChange = useCallback((v) => {
    const data = adCreateDataRef.current;
    if (!data) return;
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, comments: v }
    });
  }, [dispatch]);

  const handleGenerateSuggestedComment = useCallback(() => {
    const data = adCreateDataRef.current;
    if (!data) return;
    const text = buildSellTimberSuggestedComment({
      designation: data.selectedProperty?.designation,
      selectedAction: data.selectedAction,
      geometry,
      features: collectFeaturesFromMapSelection(geometry),
      virkesRows: volumeRows
    });
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, comments: text }
    });
  }, [dispatch, geometry, volumeRows]);

  const handleAttachmentsChange = useCallback((newFiles) => {
    const data = adCreateDataRef.current;
    if (!data) return;
    const prev = data.attachments ?? [];
    const nextIds = new Set(newFiles.map((f) => f.id));
    for (const p of prev) {
      if (p.existing && !nextIds.has(p.id)) {
        deleteAdAttachmentsNew(p.id).catch(() => {});
      }
    }
    const attachments = newFiles.map((file) => {
      file.valid = true;
      return file;
    });
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, attachments }
    });
  }, [dispatch]);

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await submitSellTimberAd({ adCreateData, geometry, forestWithSections, existingAdId });
      if (result.needsRedirect && result.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      if (result.ok) {
        window.location.assign(`/auctions/${result.adId}/`);
        return;
      }
      setSubmitError(result.message ?? SELL_TIMBER_GENERIC_ERROR);
    } catch (e) {
      console.error(e);
      setSubmitError(SELL_TIMBER_GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto flex max-w-full flex-col gap-8 p-6">
            <Text variant="text-xl/leading-normal/medium">Sammanfattning</Text>
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Kontrollera dina annonsuppgifter. Klicka på redigera-knappen på valfritt steg för att återgå till det steget.
            </Text>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium">Steg 1 - Uppgifter</Text>
              <SummaryRow
                label="Fastighet"
                value={adCreateData?.selectedProperty?.designation ?? '-'}
                valueClassName="capitalize"
              />
              <SummaryRow
                label="Typ av åtgärd"
                value={labelFor(ACTION_OPTIONS, adCreateData?.selectedAction)}
                info={
                  <SellTimberFieldInfo
                    title="Typ av åtgärd"
                    text={SELL_TIMBER_FIELD_INFO.typAvAtgard}
                    aria-label="Information om typ av åtgärd"
                  />
                }
              />
              <SummaryRow
                label="Hur du säljer virket"
                value={labelFor(SALE_OPTIONS, adCreateData?.selectedSale)}
                info={
                  <SellTimberFieldInfo
                    title={
                      SELL_TIMBER_SALE_INFO[adCreateData?.selectedSale]?.title ?? 'Hur du säljer virket'
                    }
                    text={
                      SELL_TIMBER_SALE_INFO[adCreateData?.selectedSale]?.text ??
                      SELL_TIMBER_FIELD_INFO.hurSaljaVirke
                    }
                    aria-label="Information om hur du säljer virket"
                  />
                }
              />
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                <Text
                  variant="text-sm/leading-normal/medium"
                  className="min-w-[10rem] text-charcoal-700"
                >
                  Annonsperiod
                </Text>
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-900">
                  Din annons ligger synlig i tre veckor från och med dagen Treebula publicerar den.
                </Text>
              </div>
              <SummaryEditButton
                step={1}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-2 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium">Steg 2 - Område på kartan</Text>
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Kartan visar fastighetsgränsen, valda avdelningar, avverkningsanmälningar, ritade områden samt
                eventuella markörer.
              </Text>
              <SummaryEditButton
                step={2}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>
          </div>
        </div>
      </TailwindWrapper>

      {geometry ? (
        <div
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 24px 24px'
          }}
        >
          <SellTimberMap
            selection={{
              mapMode: geometry.mapMode ?? 'sections',
              sections: geometry.sections ?? [],
              borderCoords: geometry.borderCoords ?? null,
              fellingApplicationsGeojson: geometry.fellingApplicationsGeojson ?? null,
              selectedSectionLocalIds: geometry.selectedSectionLocalIds ?? [],
              selectedFellingApplicationIds: geometry.selectedFellingApplicationIds ?? [],
              drawnPolygons: geometry.drawnPolygons ?? [],
              placedMarkers: excludeGrotMarkerFromTimberPlacedMarkers(
                geometry.placedMarkers ?? []
              ),
              customAreaFromAd: geometry.customAreaFromAd ?? null
            }}
            handlers={{}}
            options={{
              readOnly: true,
              onlySelectedOverlays: true,
              mapId: 'sell_timber_summary_leaflet',
              hydratedAdGeometry: geometry.customAreaFromAd ?? null,
              virkesLabels: virkesLabelsForMap,
              mapLabelMinZoom: SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP4
            }}
          />
        </div>
      ) : (
        <TailwindWrapper>
          <div className="mx-auto max-w-full px-6 pb-4">
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Ingen kartdata att visa.
            </Text>
          </div>
        </TailwindWrapper>
      )}

      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto flex max-w-full flex-col gap-8 p-6 pt-0">
            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="text-lg/leading-normal/medium" as="span">
                  Steg 3 - Virkesinformation
                </Text>
                <SellTimberFieldInfo
                  title="Virkesinformation"
                  text={SELL_TIMBER_FIELD_INFO.step3VirkesData}
                  aria-label="Information om volym och trädslag"
                />
              </div>
              <SellTimberVirkesResponsiveTable
                rows={volumeRows}
                showExtraCols={showExtraCols}
                formatHa={(v) =>
                  formatSvLocaleNumber(v, { maximumFractionDigits: 1, emptyLabel: '-' })
                }
                formatVolumeM3fub={(v) =>
                  formatSvLocaleNumber(v, { maximumFractionDigits: 0, emptyLabel: '-' })
                }
                pct={(v) =>
                  formatSvLocaleNumber(v, {
                    maximumFractionDigits: 0,
                    suffix: '%',
                    invalidAsEmpty: true,
                    emptyLabel: '-'
                  })
                }
                emptyMessage="Inga avdelningar i tabellen."
              />
              <SummaryEditButton
                step={3}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="text-lg/leading-normal/medium" as="span">
                  Övrig information
                </Text>
                <SellTimberFieldInfo
                  title="Övrig information"
                  text={SELL_TIMBER_FIELD_INFO.ovrigInformation}
                  aria-label="Information om övrig information"
                />
              </div>
              <div className="flex flex-col gap-3">
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Fyll i den information du vill att köparna ska ta del av gällande din virkesaffär. Du kan även generera en automatisk annonstext genom att klicka på knappen nedan.
                </Text>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="inline-flex items-center gap-2 self-start"
                  disabled={submitting || mapSelectionFeatureCount === 0}
                  onClick={handleGenerateSuggestedComment}
                  aria-label="Generera förslag på text från kartval, virkesdata och typ av åtgärd"
                >
                  Generera text
                  <Icon
                    name="SparkleSimple"
                    size="small"
                    className="shrink-0 fill-charcoal-700"
                    aria-hidden
                  />
                </Button>
              </div>
              <Textarea
                name="comments"
                value={adCreateData?.comments ?? ''}
                rows={6}
                onChange={handleCommentsChange}
                placeholder="Valfri text…"
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="text-lg/leading-normal/medium" as="span">
                  Bilagor
                </Text>
                <SellTimberFieldInfo
                  title="Bilagor"
                  text={SELL_TIMBER_FIELD_INFO.bilagor}
                  aria-label="Information om bilagor"
                />
              </div>
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Ladda upp en eller flera filer som du vill bifoga till annonsen (valfritt).
              </Text>
              <TwFileUpload
                name="attachment_files"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.txt,.odt,.ods,.gif,.zip"
                label=""
                multiple
                buttonText="Välj filer"
                value={adCreateData?.attachments ?? EMPTY_ATTACHMENTS}
                onChange={handleAttachmentsChange}
                idleText="Ladda upp en eller flera filer."
                errorText={SELL_TIMBER_GENERIC_ERROR}
                successText="Filen är uppladdad."
                disabled={submitting}
              />
            </section>

            {submitError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700">
                {submitError}
              </Text>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => dispatch({ type: 'SET_STEP', step: 3 })}
                disabled={submitting}
              >
                Tillbaka
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Slutför…' : 'Slutför'}
              </Button>
            </div>
          </div>
        </div>
      </TailwindWrapper>
    </>
  );
}
